import { getAuthToken, renewAuthToken } from "./auth";
import type { RetrievedChunkSnapshot } from "../db/types";

// The hybrid retrieval endpoint already deployed on Hierarchical-search: it runs
// the EPFL site search (index chosen server-side by SERVICENOW_OBO_SITE_INDEX,
// epfl_website_v2 since 2026-09-09) plus ServiceNow KB + resolved
// incidents (OBO, the user's real ServiceNow rights) and returns a ranked list.
// We forward the same Entra id_token (aud fe9b7ccb…, now accepted server-side)
// exactly like the ServiceNow plugin does — no token exchange.
const EPFL_HYBRID_URL = "https://hierarchical-search.epfl.ch/servicenow/search";

// Recherche « LEX seulement » (bulle LEX, 19.10.2026) : l'index du site
// restreint par le backend aux textes de Polylex — LEX, directives, annexes et
// textes fédéraux. Même jeton, même forme de réponse, plus les champs lex_*.
// Index public : pas de droits ServiceNow en jeu, donc pas d'OBO à faire.
export const EPFL_POLYLEX_URL =
  "https://hierarchical-search.epfl.ch/search/epfl_website_v2/polylex";

// Subset of the backend `SearchResultNode` we actually consume.
interface SearchResultNode {
  precise_content: string;
  context_content: string;
  score: number | null;
  title: string;
  source_url: string;
  header_path?: string | null;
  node_hierarchy?: string | null;
  page_number?: number | null;
  // Fiche de la page (06.10.2026, champs additifs du backend) : titre réel et
  // résumé d'une ligne, tirés de ses descripteurs de pages. Absents pour les
  // PDF, les résultats ServiceNow et les pages postérieures aux descripteurs.
  page_title?: string | null;
  page_summary?: string | null;
  // Route Polylex seulement : le texte réglementaire d'où vient le passage.
  // lex_type : « LEX », « DOC », « annexe » ou « Fedlex ».
  lex_number?: string | null;
  lex_title?: string | null;
  lex_type?: string | null;
}

function deriveSource(title: string): RetrievedChunkSnapshot["source"] {
  if (title.startsWith("[KB]")) return "servicenow_kb";
  if (title.startsWith("[INC]")) return "servicenow_incident";
  return "epfl_site";
}

/** Nom affiché pour un résultat du site EPFL : l'URL, pas le fichier d'index.
 *
 *  Le backend renvoie dans `title` le `file_name` du nœud, c'est-à-dire le
 *  markdown de l'index : « page_4d076e0e.md ». C'est un identifiant interne, il
 *  ne dit rien à l'usager et ne l'aide pas à retrouver la source. L'URL réelle
 *  est déjà là, dans `source_url`.
 *
 *  On l'allège de ce qui n'apporte rien à la lecture — protocole, « www. »,
 *  barre finale — pour tenir dans la largeur du panneau sans perdre
 *  l'information. Les résultats ServiceNow, eux, portent un vrai titre
 *  (« [KB] M365 - FAQ ») : on n'y touche pas.
 */
function nomAffiche(node: SearchResultNode): string {
  const brut = (node.source_url || "").trim();
  if (!brut) return node.title;
  try {
    const u = new URL(brut);
    const chemin = u.pathname.replace(/\/$/, "");
    return u.host.replace(/^www\./, "") + chemin + (u.hash || "");
  } catch {
    // URL non analysable : mieux vaut la montrer telle quelle qu'un nom de
    // fichier d'index.
    return brut;
  }
}

/** Écarte les sections vides de sens (« / », chaîne vide) pour ne pas afficher
 *  « §/ » en fin de libellé. */
function normaliserSection(section: string | null | undefined): string | null {
  const s = (section || "").trim();
  return !s || s === "/" ? null : s;
}

/** « LEX 4.1.4 — Règlement concernant la gestion du temps de travail », pour un
 *  passage de la route Polylex ; null sinon. Le nom de fichier des PDF de
 *  Polylex (« 4.1.4_r_gestion_temps_travail_fr.pdf ») ne dit rien au lecteur. */
function nomLex(node: SearchResultNode): string | null {
  const titre = (node.lex_title || "").trim();
  if (!titre) return null;
  const numero = (node.lex_number || "").trim();
  const type = (node.lex_type || "").trim();
  const prefixe =
    type === "LEX" || type === "DOC"
      ? `${type} ${numero}`
      : type === "annexe" && numero
        ? `Annexe LEX ${numero}`
        : numero;
  return prefixe.trim() ? `${prefixe.trim()} — ${titre}` : titre;
}

function toSnapshot(node: SearchResultNode, i: number): RetrievedChunkSnapshot {
  // `context_content` is the larger chunk — that's where the verbatim sentence
  // the LLM cites lives, consistent with how local chunks are highlighted.
  const text = node.context_content || node.precise_content || "";
  const url = node.source_url || `epfl-hybrid:${i}`;
  return {
    // Placeholder — chat.ts renumbers the whole merged corpus before prompting.
    number: 0,
    docId: url,
    chunkId: url,
    // Les résultats ServiceNow portent un vrai titre ; ceux du site EPFL n'ont
    // qu'un nom de fichier d'index, remplacé par le titre réel de la page quand
    // le backend le connaît (`page_title`), sinon par l'URL. L'URL reste montrée
    // au LLM dans la fiche du passage (renderChunksBlock).
    docName:
      deriveSource(node.title) === "epfl_site"
        ? nomLex(node) || (node.page_title || "").trim() || nomAffiche(node)
        : node.title,
    summary: (node.page_summary || "").trim() || undefined,
    page: node.page_number ?? 0,
    // `header_path` vaut « / » à la racine d'un document : afficher « §/ »
    // n'apprend rien et encombre la ligne.
    section: normaliserSection(node.header_path ?? node.node_hierarchy),
    text,
    score: node.score ?? 0,
    source: deriveSource(node.title),
    sourceUrl: node.source_url || undefined,
  };
}

/** La recherche EPFL n'a pas pu se faire : le message est montré à l'usager à
 *  la place du nombre de résultats (journal d'activité, cf. `tracked` dans
 *  state/chat.ts). Avant le 06.10.2026, tout échec devenait « 0 résultat » —
 *  indiscernable d'une recherche qui ne trouve rien, et l'usager ne pouvait
 *  pas deviner qu'il suffisait de se reconnecter. */
export class EpflSearchUnavailable extends Error {}

export const MSG_RECONNECTER = "Recherche EPFL indisponible — reconnectez-vous";
export const MSG_INDISPONIBLE = "Recherche EPFL indisponible";

/** Refus lié au jeton : 401, ou 502 « userinfo lookup failed » (l'annuaire EPFL
 *  ne reconnaît pas l'usager à partir de son id_token). Un 502 d'une autre
 *  origine (passerelle, pod indisponible) n'est pas une affaire de jeton. */
async function refusDuJeton(res: Response): Promise<boolean> {
  if (res.status === 401) return true;
  if (res.status !== 502) return false;
  try {
    return /userinfo/i.test(await res.clone().text());
  } catch {
    return false;
  }
}

/** Query the EPFL hybrid retrieval endpoint as the signed-in user (OBO) and map
 *  the results to the local snapshot shape. The local RAG must keep working
 *  whatever happens here — this is a supplement, never a hard dependency — so
 *  failures never reject the caller's Promise.all: they throw
 *  `EpflSearchUnavailable`, which `tracked` (state/chat.ts) turns into `[]` plus
 *  a visible message. Aborts are re-thrown as-is. */
export function searchEpflHybrid(
  query: string,
  topK: number,
  signal?: AbortSignal,
): Promise<RetrievedChunkSnapshot[]> {
  return rechercherEpfl(EPFL_HYBRID_URL, query, topK, signal);
}

/** Même contrat que `searchEpflHybrid`, limité aux textes de Polylex. */
export function searchEpflPolylex(
  query: string,
  topK: number,
  signal?: AbortSignal,
): Promise<RetrievedChunkSnapshot[]> {
  return rechercherEpfl(EPFL_POLYLEX_URL, query, topK, signal);
}

async function rechercherEpfl(
  url: string,
  query: string,
  topK: number,
  signal?: AbortSignal,
): Promise<RetrievedChunkSnapshot[]> {
  const appeler = (idToken: string) =>
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ query, rerank: true, top_k: topK }),
      signal,
    });
  try {
    let idToken: string;
    try {
      idToken = await getAuthToken();
    } catch {
      throw new EpflSearchUnavailable(MSG_RECONNECTER);
    }
    let res = await appeler(idToken);
    if (await refusDuJeton(res)) {
      // Jeton refusé alors qu'il nous semblait valide : en obtenir un neuf
      // (reconnexion silencieuse, comme un « déco-reco ») et réessayer une fois.
      const neuf = await renewAuthToken();
      if (!neuf) throw new EpflSearchUnavailable(MSG_RECONNECTER);
      res = await appeler(neuf);
      if (await refusDuJeton(res)) throw new EpflSearchUnavailable(MSG_RECONNECTER);
    }
    if (!res.ok) {
      console.warn(`EPFL hybrid search failed: ${res.status} ${res.statusText}`);
      throw new EpflSearchUnavailable(MSG_INDISPONIBLE);
    }
    const nodes = (await res.json()) as SearchResultNode[];
    if (!Array.isArray(nodes)) throw new EpflSearchUnavailable(MSG_INDISPONIBLE);
    return nodes.map(toSnapshot);
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    if (e instanceof EpflSearchUnavailable) throw e;
    console.warn("EPFL hybrid search error:", e);
    throw new EpflSearchUnavailable(MSG_INDISPONIBLE);
  }
}
